import { FC, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { LayerGroup, useMap } from "react-leaflet";
import {
	BaseNodeDTO,
	NetworkNodeRegistryDTO,
	NodeDetailsDTO,
	NodesMapItemDTO,
	NodesPageItemDTO,
} from "../../../../../datasources/api-client/data-contracts";
import "./style.css";
import React from "react";
import L, { LatLngExpression } from "leaflet";
import { useTranslation } from "react-i18next";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { NoPinIcon } from "../../../../../assets/icons";
import { ThemeColors } from "../../../../../theme/themeValues";
import { markerIcon } from "../../utils";
import { NodeGroupMarker } from "./NodeGroupMarker";
import { NodeMarker } from "./NodeMarker";

type MarkerNode = NetworkNodeRegistryDTO | NodesPageItemDTO | NodeDetailsDTO | NodesMapItemDTO;

interface Props {
	nodes?: MarkerNode[];
	color: string;
	showDetailsOnClick?: boolean;
	onSelectNode?: (node: BaseNodeDTO) => void;
	onNavigate?: (node: MarkerNode) => void;
}

const MarkerLayer: FC<Props> = ({ nodes, color, showDetailsOnClick, onSelectNode, onNavigate }) => {
	const { t } = useTranslation("common");

	const markersRef = useRef<Record<number, L.Marker>>({});

	const map = useMap();
	const handleNodeGroupClick = (lat: number, lng: number) => {
		if (!map) return;
		const position = [lat, lng] as LatLngExpression;
		const zoomLevel = Math.min(map.getZoom() + 1, map.getMaxZoom()); // Zoom in respecting max zoom
		map.setView(position, zoomLevel, { animate: true });
	};

	if (nodes?.length === 0 || nodes === undefined) {
		return (
			<Box
				sx={{
					cursor: "default",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
				}}
				width={"fit-content"}
				display={"flex"}
				justifyContent={"center"}
				alignItems={"center"}
				position={"relative"}
				zIndex={1000}
			>
				<Stack justifyContent={"center"} alignItems={"center"}>
					<Box
						sx={{
							width: "32px",
							height: "32px",
							borderRadius: "50%",
							backgroundColor: ThemeColors.LightContrastText,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<NoPinIcon />
					</Box>
					<Stack
						sx={{ backgroundColor: ThemeColors.LightContrastText }}
						spacing={1}
						justifyContent={"center"}
						p={2}
						borderRadius={2}
						mt={1}
					>
						<Typography textAlign={"center"} variant="h6">
							{t("no location data found")}
						</Typography>
						<Typography textAlign={"center"} variant="body2">
							{t("location information is not yet available")}
						</Typography>
					</Stack>
				</Stack>
			</Box>
		);
	}

	const nodesTotalAmount = nodes.reduce((acc, node) => acc + ("nodeCount" in node ? node.nodeCount : 1), 0);

	return (
		<LayerGroup>
			<MarkerClusterGroup
				showCoverageOnHover={false}
				maxClusterRadius={2} // 2px radius to completely disable clustering while keeping spiferfying
				iconCreateFunction={() => markerIcon(color)}
			>
				{nodes.map((node, index) => {
					if (!node.lat || !node.lng) return null;

					if ("nodeCount" in node && node.nodeCount > 1)
						return (
							<NodeGroupMarker
								key={index}
								index={index}
								nodeGroup={node}
								markers={markersRef}
								color={color}
								totalNodeAmount={nodesTotalAmount}
								onMarkerClick={handleNodeGroupClick}
							/>
						);

					return (
						<NodeMarker
							showDetailsOnClick={showDetailsOnClick}
							key={index}
							index={index}
							node={node}
							markers={markersRef}
							color={color}
							onSelectNode={onSelectNode}
							onNavigate={onNavigate}
						/>
					);
				})}
			</MarkerClusterGroup>
		</LayerGroup>
	);
};

export default React.memo(MarkerLayer, (prev, next) => prev.nodes === next.nodes && prev.color === next.color);
